import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../ThemeContext";
import "../styles/tokens.css";
import "./primitives.css";

// Shared v2 primitives. Small on purpose: these exist so screens stop hand-rolling the
// same button, empty state and confirm dialog five different ways, which is what v1
// does today.

export const Button = ({ variant, icon, className = "", children, ...rest }) => (
  <button
    type="button"
    className={`v2-btn${variant ? ` v2-btn--${variant}` : ""}${icon ? " v2-btn--icon" : ""} ${className}`.trim()}
    {...rest}
  >
    {children}
  </button>
);

// Screens that own a search box or a primary action put it in the top bar rather than
// repeating a page header underneath one, which is how the design kit lays them out.
// The slot is a portal target the shell renders; nothing happens if a screen omits it.
export const TopbarSlot = ({ children }) => {
  const [host, setHost] = useState(null);
  useEffect(() => { setHost(document.getElementById("v2-topbar-slot")); }, []);
  return host ? createPortal(children, host) : null;
};

export const Chip = ({ children, className = "", ...rest }) => (
  <span className={`v2-chip ${className}`.trim()} {...rest}>{children}</span>
);

export const Segmented = ({ options, value, onChange, ariaLabel }) => (
  <div className="v2-seg" role="tablist" aria-label={ariaLabel}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="tab"
        aria-selected={value === o.value}
        className={value === o.value ? "on" : ""}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// The kit uses a switch, not a checkbox, wherever a setting is on or off. Same
// control everywhere means "on" always looks the same.
export const Toggle = ({ checked, onChange, label, disabled, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel || label}
    disabled={disabled}
    className={`v2-toggle${checked ? " on" : ""}`}
    onClick={() => onChange(!checked)}
  />
);

// A settings row: label on the left, control pushed to the right.
export const SettingRow = ({ name, desc, children }) => (
  <div className="v2-setting-row">
    <div>
      <div className="v2-setting-name">{name}</div>
      {desc && <div className="v2-setting-desc">{desc}</div>}
    </div>
    {children}
  </div>
);

export const Swatches = ({ colors, value, onChange }) => (
  <div className="v2-swatches">
    {colors.map((c) => (
      <button
        key={c}
        type="button"
        aria-label={`Accent ${c}`}
        aria-pressed={value === c}
        className={`v2-sw${value === c ? " on" : ""}`}
        style={{ background: c }}
        onClick={() => onChange(c)}
      />
    ))}
  </div>
);

// Hint renders AFTER the control on purpose. With it between the label and the input,
// a field carrying a hint pushed its control a line lower than the field beside it, so
// any two-column row of fields had misaligned inputs whenever only one had helper text.
export const Field = ({ label, hint, error, children }) => (
  <div className="v2-field">
    {label && <label>{label}</label>}
    {children}
    {hint && <span className="v2-hint">{hint}</span>}
    {error && <span className="v2-alert v2-alert--error">{error}</span>}
  </div>
);

export const Alert = ({ tone = "error", children }) =>
  children ? <div className={`v2-alert v2-alert--${tone}`}>{children}</div> : null;

export const EmptyState = ({ title, children, action }) => (
  <div className="v2-empty">
    <h4>{title}</h4>
    {children && <p>{children}</p>}
    {action}
  </div>
);

// Skeleton rows rather than a spinner: the table's shape is already known, so the
// layout should not jump when data lands.
export const SkeletonRows = ({ rows = 5, cols = 4, widths = [] }) =>
  [...Array(rows)].map((_, r) => (
    <tr key={`sk-${r}`} className="v2-skel">
      {[...Array(cols)].map((__, c) => (
        <td key={c}><span style={{ width: widths[c] || `${40 + ((c * 17) % 45)}%` }} /></td>
      ))}
    </tr>
  ));

// The portal host for modals.
//
// A modal cannot render in place — see the note in Modal below — but it also cannot
// render into a bare document.body: every v2 token is declared on `.v2`, not on :root,
// so that the v2 palette can never leak into v1 while both shells share a document.
// Outside `.v2` each var(--v2-*) resolves to nothing, which paints the dialog with no
// background at all — transparent, with the page showing straight through it.
//
// So the host is a div appended to body that carries the same `v2` (+ `dark`) classes
// the shell root does. One host, reused by every modal.
const usePortalHost = (isDarkMode) => {
  const [host, setHost] = useState(null);

  useEffect(() => {
    let node = document.getElementById("v2-portal");
    if (!node) {
      node = document.createElement("div");
      node.id = "v2-portal";
      document.body.appendChild(node);
    }
    setHost(node);
    // Deliberately not removed on unmount: it is shared by every modal, and tearing it
    // down would pull the rug from under any dialog still open.
  }, []);

  // Follows the theme toggle rather than being set once, so switching to dark with a
  // dialog open repaints it instead of leaving it on the light palette.
  useEffect(() => {
    if (host) host.className = `v2${isDarkMode ? " dark" : ""}`;
  }, [host, isDarkMode]);

  return host;
};

export const Modal = ({ title, onClose, children, footer, wide }) => {
  const { isDarkMode } = useTheme();
  const host = usePortalHost(isDarkMode);

  // Escape closes. A modal you can only leave with the mouse is a trap for anyone
  // driving by keyboard.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Rendered through the portal host above rather than in place. A modal is nearly
  // always opened from inside a table, an expanded row or a drawer, and several of those
  // ancestors are `position: sticky` — which ALWAYS creates a stacking context, even at
  // z-index auto. That traps the scrim's z-index inside the ancestor, so a sticky table
  // header at z-index 1 paints over a dialog asking for 1080. The portal takes the
  // dialog out of that subtree entirely, so its z-index is measured against the page.
  const body = (
    <div className="v2-scrim" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="v2-modal" style={wide ? { maxWidth: "44rem" } : undefined}>
        <div className="v2-modal-head">
          <h4>{title}</h4>
          <Button icon aria-label="Close" style={{ marginLeft: "auto" }} onClick={onClose}>✕</Button>
        </div>
        <div className="v2-modal-body">{children}</div>
        {footer && <div className="v2-modal-foot">{footer}</div>}
      </div>
    </div>
  );

  return host ? createPortal(body, host) : null;
};

export const ConfirmModal = ({ title, body, confirmLabel = "Delete", onConfirm, onClose, busy }) => (
  <Modal
    title={title}
    onClose={onClose}
    footer={
      <>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={onConfirm} disabled={busy}>
          {busy ? "Working…" : confirmLabel}
        </Button>
      </>
    }
  >
    <p style={{ margin: 0, fontSize: ".8125rem", color: "var(--v2-text-secondary)" }}>{body}</p>
  </Modal>
);

// A sortable column header. Returned as a hook-ish helper because every v2 table needs
// the same click-to-toggle behaviour and the same arrow.
export const SortHeader = ({ label, colKey, sort, onSort, className }) => (
  <th className={className}>
    <button type="button" onClick={() => onSort(colKey)} aria-label={`Sort by ${label}`}>
      {label}
      <span className="v2-sortmark" aria-hidden="true">
        {sort.key === colKey ? (sort.dir === "asc" ? "↑" : "↓") : ""}
      </span>
    </button>
  </th>
);
